import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  dataToField,
  dateBoundsOf,
  dateKindOf,
  type CustomFieldConfig,
  dayStringToLocal,
  fieldDto,
  isChoiceFieldType,
  isCustomFieldValueBlank,
  isDayString,
  isDayWithinDateBounds,
  MAX_NUMBER_DECIMAL_PLACES,
  toDayString,
} from "@salestrak/custom-fields";
import {
  Box,
  Button,
  DatePicker,
  Field,
  FieldError,
  FieldLabel,
  Icon,
  Input,
  OptionSelect,
  Separator,
  Text,
  Textarea,
  Toggle,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";

import {
  ChoiceOptionsEditor,
  asOptionSortMode,
  makeOptionId,
  optionValueOf,
  type OptionRow,
  type OptionSortMode,
} from "#app/components/custom-fields/choice-options-editor";
import { CustomFieldRenderer } from "#app/components/custom-fields/custom-field-renderers";
import { FieldDatePicker } from "#app/components/custom-fields/field-date-picker";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import {
  AnswerLinkSettings,
  type CreateFieldDoor,
  type ItemRelink,
} from "#app/components/forms/form-builder/form-answer-link";
import {
  ConditionalSettings,
  ConditionNote,
} from "#app/components/forms/form-builder/form-conditions";
import { formItemGlyph } from "#app/components/forms/form-builder/form-item-identity";
import {
  PaneScrollGutter,
  PaneScroller,
  SettingsSection,
  SwitchRow,
} from "#app/components/forms/form-builder/settings-rows";
import { FieldTypeGlyph } from "#app/components/settings/field-type-glyph";
import { isMultipleField } from "#app/lib/custom-field-utils";
import { localId } from "#app/lib/local-id";
import {
  answerOptionValues,
  choiceCount,
  formItemNotice,
  isCheckboxItem,
  isFieldItem,
  linkedPrefills,
  resolveItemField,
  stringIds,
  defaultsToNow,
  defaultValueError,
  TYPED_DEFAULT_TYPES,
  type Form,
  type FormCondition,
  type FormItem,
  type FormLinkedItem,
} from "#app/lib/forms";

type ItemPatch = (item: FormItem) => FormItem;

const DATE_FORMAT_OPTIONS = [
  { label: "Date", value: "date" },
  { label: "Date and time", value: "dateTime" },
];

function formItemNoticeSentence(notice: NonNullable<ReturnType<typeof formItemNotice>>): string {
  return notice.kind === "hidden"
    ? `Not shown on the form. ${notice.reason}.`
    : `Rule ignored. ${notice.reason}.`;
}

function patchInlineConfig(
  patch: Partial<NonNullable<CustomFieldDef["config"]>>,
): ItemPatch {
  return (item) => {
    if (item.kind !== "question" && item.kind !== "attachment") return item;
    const config = { ...item.field.config, ...patch } as CustomFieldDef["config"];
    return { ...item, field: { ...item.field, config } };
  };
}

function ChoiceSettings({
  item,
  onChange,
  onRemoveChoices,
}: {
  item: Extract<FormItem, { kind: "question" }>;
  onChange: (patch: ItemPatch) => void;
  /** Writes the choices when one was deleted, so rules naming it change in the same step. */
  onRemoveChoices: (patch: ItemPatch) => void;
}) {
  const multiple = item.field.type === "labels";
  const [options, setOptions] = useState<OptionRow[]>(() =>
    (item.field.config?.options ?? []).map((option) => ({
      color: option.color ?? null,
      id: makeOptionId(),
      label: option.label ?? "",
      value: option.value ?? undefined,
    })),
  );

  // Write-through runs after the rows commit, not inside the updater: updaters run during render.
  const rowsDirty = useRef(false);
  const rowRemoved = useRef(false);
  useEffect(() => {
    if (!rowsDirty.current) return;
    rowsDirty.current = false;
    const write = rowRemoved.current ? onRemoveChoices : onChange;
    rowRemoved.current = false;
    write(
      patchInlineConfig({
        options: options
          .filter((row) => row.label.trim())
          .map((row) => ({
            __typename: "CustomFieldOptionView" as const,
            color: null,
            label: row.label.trim(),
            value: optionValueOf(row),
          })),
      }),
    );
  }, [onChange, onRemoveChoices, options]);
  // A new choice value is fixed at creation, never derived from its label, so renames keep rules.
  const setOptionsAndWrite: Dispatch<SetStateAction<OptionRow[]>> = (next) => {
    rowsDirty.current = true;
    setOptions((prev) => {
      const rows = typeof next === "function" ? next(prev) : next;
      if (prev.some((row) => row.label.trim() && !rows.some((kept) => kept.id === row.id))) {
        rowRemoved.current = true;
      }
      return rows.map((row) => (row.value ? row : { ...row, value: localId() }));
    });
  };

  const defaults = useMemo(
    () => answerOptionValues(item.field.config?.defaultValue),
    [item.field.config?.defaultValue],
  );

  return (
    <ChoiceOptionsEditor
      colors={false}
      defaults={defaults}
      exclusiveDefault={!multiple}
      onDefaultsChange={(next) =>
        onChange(
          patchInlineConfig({
            defaultValue: (multiple ? next : (next[0] ?? null)) as NonNullable<
              CustomFieldDef["config"]
            >["defaultValue"],
          }),
        )
      }
      onOptionsChange={setOptionsAndWrite}
      onSortChange={(mode: OptionSortMode) =>
        onChange(patchInlineConfig({ optionSort: mode }))
      }
      options={options}
      sort={asOptionSortMode(item.field.config?.optionSort)}
    />
  );
}

function LinkedChoiceSettings({
  item,
  field,
  onChange,
}: {
  item: Extract<FormItem, { kind: "customField" | "property" }>;
  field: CustomFieldDef;
  onChange: (patch: ItemPatch) => void;
}) {
  const rows = useMemo<OptionRow[]>(
    () =>
      (field.config?.options ?? []).flatMap((option) =>
        option?.value
          ? [{ color: option.color ?? null, id: option.value, label: option.label ?? "", value: option.value }]
          : [],
      ),
    [field.config?.options],
  );
  return (
    <ChoiceOptionsEditor
      colors={false}
      defaults={item.defaultChoices ?? []}
      exclusiveDefault={field.type === "select"}
      onDefaultsChange={(next) =>
        onChange((current) => {
          if (current.kind !== "customField" && current.kind !== "property") return current;
          const { defaultChoices: _previous, ...rest } = current;
          return next.length > 0 ? { ...rest, defaultChoices: next } : rest;
        })
      }
      onOptionsChange={() => {}}
      onSortChange={() => {}}
      options={rows}
      readOnly
      sort="manual"
    />
  );
}

/** Min and Max never cross: raising one past the other carries it along. */
function LimitRange({
  noun,
  min,
  max,
  count,
  maxFloor,
  ceiling,
  onChange,
}: {
  noun: string;
  min: number | null;
  max: number | null;
  count: boolean;
  maxFloor?: number | undefined;
  ceiling?: number | undefined;
  onChange: (next: { min: number | null; max: number | null }) => void;
}) {
  const cap = ceiling !== undefined && ceiling >= 1 ? ceiling : null;
  const parse = (raw: string): number | null => {
    if (raw.trim() === "") return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    if (!count) return value;
    const whole = Math.floor(value);
    if (whole < 1) return null;
    return cap === null ? whole : Math.min(whole, cap);
  };
  const edge = (
    label: string,
    value: number | null,
    write: (next: number | null) => void,
    floor?: number,
  ) => (
    <Field fill>
      <FieldLabel>{label}</FieldLabel>
      <Input
        inputMode={count ? "numeric" : "decimal"}
        max={cap ?? undefined}
        min={floor ?? (count ? 1 : undefined)}
        onBlur={floor === undefined || value === null || value >= floor ? undefined : () => write(floor)}
        onChange={(event) => write(parse(event.target.value))}
        placeholder="—"
        step={count ? 1 : "any"}
        type="number"
        value={value ?? ""}
      />
    </Field>
  );
  return (
    <Box direction="column" gap="lg">
      {edge(`Min ${noun}`, min, (next) =>
        onChange({ max: next !== null && max !== null && next > max ? next : max, min: next }),
      )}
      {edge(
        `Max ${noun}`,
        max,
        (next) =>
          onChange({ max: next, min: next !== null && min !== null && next < min ? next : min }),
        maxFloor,
      )}
    </Box>
  );
}

function ConfigRange({
  item,
  noun,
  count,
  maxFloor,
  ceiling,
  onChange,
}: {
  item: Extract<FormItem, { kind: "question" | "attachment" }>;
  noun: string;
  count: boolean;
  maxFloor?: number | undefined;
  ceiling?: number | undefined;
  onChange: (patch: ItemPatch) => void;
}) {
  return (
    <LimitRange
      ceiling={ceiling}
      count={count}
      maxFloor={maxFloor}
      max={item.field.config?.max ?? null}
      min={item.field.config?.min ?? null}
      noun={noun}
      onChange={(next) => onChange(patchInlineConfig(next))}
    />
  );
}

function DefaultValueSetting({
  item,
  onChange,
}: {
  item: Extract<FormItem, { kind: "question" }>;
  onChange: (patch: ItemPatch) => void;
}) {
  const { field } = item;
  const dateKind = dateKindOf(field);
  if (dateKind === "date" || dateKind === "dateTime") {
    return <DateDefaultSetting item={item} onChange={onChange} withTime={dateKind === "dateTime"} />;
  }
  return <TypedDefaultSetting item={item} onChange={onChange} />;
}

function TypedDefaultSetting({
  item,
  onChange,
}: {
  item: Extract<FormItem, { kind: "question" }>;
  onChange: (patch: ItemPatch) => void;
}) {
  const { field } = item;
  const [editing, setEditing] = useState(false);
  const error = editing ? null : defaultValueError(item);
  return (
    <Field fill invalid={error !== null}>
      <FieldLabel>Default value</FieldLabel>
      <Box fill="width" onBlur={() => setEditing(false)} onFocus={() => setEditing(true)}>
        <CustomFieldRenderer
          field={field}
          formAnswer
          invalid={error !== null}
          onChangeValue={(formValue) => {
            const next = fieldDto(field.type, formValue);
            onChange(
              patchInlineConfig({
                defaultValue: (isCustomFieldValueBlank(next) ? null : next) as NonNullable<
                  CustomFieldDef["config"]
                >["defaultValue"],
              }),
            );
          }}
          surface="default"
          value={dataToField(field.type, field.config?.defaultValue ?? null).fieldValue}
        />
      </Box>
      {error ? <FieldError match>{error}</FieldError> : null}
    </Field>
  );
}

function NowToggle({
  word,
  label,
  tooltip,
  pressed,
  onPressedChange,
}: {
  word: string;
  label: string;
  tooltip: string;
  pressed: boolean;
  onPressedChange: (on: boolean) => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Toggle aria-label={label} onPressedChange={onPressedChange} pressed={pressed} size="xs" />
        }
      >
        <Text size="xs" variant={pressed ? undefined : "muted"}>
          {word}
        </Text>
      </TooltipTrigger>
      <TooltipPopup>{tooltip}</TooltipPopup>
    </Tooltip>
  );
}

function DateDefaultSetting({
  item,
  onChange,
  withTime,
}: {
  item: Extract<FormItem, { kind: "question" }>;
  onChange: (patch: ItemPatch) => void;
  withTime: boolean;
}) {
  const { field } = item;
  const now = defaultsToNow(field.config);
  const word = withTime ? "Now" : "Today";
  const fixedError = now ? null : defaultValueError(item);
  const bounds = dateBoundsOf(field.config);
  const write = (patch: { defaultNow: true | null; defaultValue: unknown }) =>
    onChange(patchInlineConfig(patch as Partial<NonNullable<CustomFieldDef["config"]>>));
  return (
    <Field fill invalid={fixedError !== null}>
      <Box align="center" direction="row" fill="width" gap="xs" minHeight={24}>
        <Box grow minWidth={0}>
          <FieldLabel>Default value</FieldLabel>
        </Box>
        <NowToggle
          label={`Default value: ${word.toLowerCase()}`}
          onPressedChange={(on) => write({ defaultNow: on ? true : null, defaultValue: null })}
          pressed={now}
          tooltip="When the form is started"
          word={word}
        />
      </Box>
      <FieldDatePicker
        ariaLabel="Default value"
        clearLabel="Clear default value"
        dateFormat={now ? `'${word}'` : undefined}
        disabledDates={
          bounds.earliest || bounds.latest
            ? (day: Date) => !isDayWithinDateBounds(day, field.config)
            : undefined
        }
        field={field}
        onClear={() => write({ defaultNow: null, defaultValue: null })}
        onValueChange={(next) => {
          const value = fieldDto(field.type, next);
          write({ defaultNow: null, defaultValue: isCustomFieldValueBlank(value) ? null : value });
        }}
        placeholder="—"
        value={now ? new Date() : (field.config?.defaultValue ?? null)}
        variant="field"
        words={!now}
      />
      {fixedError ? <FieldError match>{fixedError}</FieldError> : null}
    </Field>
  );
}

function RequiredChoicesSetting({
  item,
  field,
  onChange,
}: {
  item: Extract<FormItem, { kind: "customField" | "property" | "question" }>;
  field: CustomFieldDef;
  onChange: (patch: ItemPatch) => void;
}) {
  const multiple = field.type === "labels";
  const label = multiple ? "Required choices" : "Required choice";
  const picker = useMemo<CustomFieldDef>(
    () => ({
      ...field,
      config: field.config
        ? { ...field.config, allowOptionCreate: false, defaultValue: null, showOptionColors: false }
        : field.config,
    }),
    [field],
  );
  return (
    <Field fill>
      <FieldLabel>{label}</FieldLabel>
      <Box fill="width">
        <CustomFieldRenderer
          field={picker}
          onChangeValue={(formValue) => {
            const next = fieldDto(field.type, formValue);
            const ids = stringIds(next);
            onChange((current) => {
              if (
                current.kind !== "customField" &&
                current.kind !== "property" &&
                current.kind !== "question"
              ) {
                return current;
              }
              const { requiredChoices: _requiredChoices, ...rest } = current;
              if (ids.length === 0) return rest;
              const max = rest.kind === "question" ? rest.field.config?.max : null;
              if (rest.kind === "question" && typeof max === "number" && ids.length > max && rest.field.config) {
                return {
                  ...rest,
                  field: { ...rest.field, config: { ...rest.field.config, max: ids.length } },
                  requiredChoices: ids,
                };
              }
              return { ...rest, requiredChoices: ids };
            });
          }}
          surface="default"
          value={dataToField(field.type, item.requiredChoices ?? []).fieldValue}
        />
      </Box>
    </Field>
  );
}

function PrefillSetting({
  item,
  onChange,
}: {
  item: FormLinkedItem;
  onChange: (patch: ItemPatch) => void;
}) {
  const on = linkedPrefills(item);
  return (
    <SwitchRow
      checked={on}
      info="Starts with the linked field's current value."
      label="Linked prefill"
      onCheckedChange={(next) =>
        onChange((current) => {
          if (current.kind !== "customField" && current.kind !== "property") return current;
          const { prefill: _prefill, ...rest } = current;
          return next ? { ...rest, prefill: true } : rest;
        })
      }
    />
  );
}

function wholeNumbersOnly(item: Extract<FormItem, { kind: "question" }>): boolean {
  return (item.field.config as CustomFieldConfig | null | undefined)?.wholeNumbers === true;
}

function DecimalsSetting({
  item,
  onChange,
}: {
  item: Extract<FormItem, { kind: "question" }>;
  onChange: (patch: ItemPatch) => void;
}) {
  return (
    <Field fill>
      <FieldLabel>Decimals</FieldLabel>
      <Input
        inputMode="numeric"
        max={MAX_NUMBER_DECIMAL_PLACES}
        min={0}
        onChange={(event) => {
          const raw = event.target.value;
          const places = Math.floor(Number(raw));
          onChange(
            patchInlineConfig({
              decimalPlaces:
                raw.trim() === "" || !Number.isFinite(places) ? null : Math.min(MAX_NUMBER_DECIMAL_PLACES, Math.max(0, places)),
            }),
          );
        }}
        placeholder="—"
        type="number"
        value={item.field.config?.decimalPlaces ?? ""}
      />
    </Field>
  );
}

function DateLimitSettings({
  item,
  onChange,
}: {
  item: Extract<FormItem, { kind: "question" }>;
  onChange: (patch: ItemPatch) => void;
}) {
  const { earliest, latest } = dateBoundsOf(item.field.config);
  const withTime = item.field.config?.includeTime === true;
  const edge = (side: "earliest" | "latest") => {
    const bound = side === "earliest" ? earliest : latest;
    const daysKey = side === "earliest" ? "min" : "max";
    const fixedKey = side === "earliest" ? "minDate" : "maxDate";
    const label = `${side === "earliest" ? "Earliest" : "Latest"} date${withTime ? " & time" : ""}`;
    const today = bound?.kind === "today";
    const write = (patch: { days: number | null; fixed: string | null }) =>
      onChange(patchInlineConfig({ [daysKey]: patch.days, [fixedKey]: patch.fixed }));
    const fixed =
      bound?.kind === "fixed"
        ? isDayString(bound.value)
          ? dayStringToLocal(bound.value)
          : new Date(bound.value)
        : null;
    const writeFixed = (next: Date) =>
      write({ days: null, fixed: withTime ? next.toISOString() : toDayString(next) });
    return (
      <Field fill>
        <Box align="center" direction="row" fill="width" gap="xs" minHeight={24}>
          <Box grow minWidth={0}>
            <FieldLabel>{label}</FieldLabel>
          </Box>
          <NowToggle
            label={`${label}: today`}
            onPressedChange={(on) => write({ days: on ? 0 : null, fixed: null })}
            pressed={today}
            tooltip="Today's date"
            word="Today"
          />
        </Box>
        <Box direction="row" fill="width" gap="sm">
          <Box grow minWidth={0}>
            <DatePicker
              ariaLabel={withTime ? `${label}: day` : label}
              clearLabel={`Clear ${label}`}
              dateFormat={today ? "'Today'" : undefined}
              disabledDates={(day) => {
                const other = side === "earliest" ? latest : earliest;
                if (!other) return false;
                const days = other.kind === "today" ? other.days : null;
                const otherFixed = other.kind === "fixed" ? other.value : null;
                return !isDayWithinDateBounds(
                  day,
                  side === "earliest"
                    ? { max: days, maxDate: otherFixed }
                    : { min: days, minDate: otherFixed },
                );
              }}
              mode="date"
              onClear={() => write({ days: null, fixed: null })}
              onValueChange={(next) => {
                if (next === null) {
                  write({ days: null, fixed: null });
                  return;
                }
                const at = new Date(next);
                if (fixed) at.setHours(fixed.getHours(), fixed.getMinutes(), 0, 0);
                writeFixed(at);
              }}
              placeholder="—"
              value={today ? new Date() : fixed}
              variant="field"
            />
          </Box>
          {withTime ? (
            <Box shrink={false} width={120}>
              <DatePicker
                ariaLabel={`${label}: time`}
                disabled={!fixed}
                mode="time"
                onValueChange={(next) => {
                  if (!fixed || next === null) return;
                  const at = new Date(fixed);
                  at.setHours(next.getHours(), next.getMinutes(), 0, 0);
                  writeFixed(at);
                }}
                placeholder={today ? "All day" : "—"}
                value={fixed}
                variant="field"
              />
            </Box>
          ) : null}
        </Box>
      </Field>
    );
  };
  return (
    <>
      {edge("earliest")}
      {edge("latest")}
    </>
  );
}

function PhotoCountSettings({
  item,
  onChange,
}: {
  item: Extract<FormItem, { kind: "attachment" }>;
  onChange: (patch: ItemPatch) => void;
}) {
  const multiple = isMultipleField(item.field.config);
  return (
    <>
      <SwitchRow
        checked={multiple}
        label="Allow multiple photos"
        onCheckedChange={(on) =>
          onChange(patchInlineConfig(on ? { multiple: true } : { max: null, min: null, multiple: false }))
        }
      />
      {multiple ? (
        <ConfigRange count item={item} maxFloor={2} noun="photos" onChange={onChange} />
      ) : null}
    </>
  );
}

export function FormItemSettings({
  item,
  form,
  customFieldsById,
  onChange,
  onRelink,
  onRemoveChoices,
  onCreateField,
  onInsert,
  onAttach,
  onSelect,
  onRule,
  onDelete,
  onRelease,
  onReplace,
  onClose,
}: {
  item: FormItem;
  form: Pick<Form, "name" | "items" | "settings">;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  onChange: (patch: ItemPatch) => void;
  onRelink: (relink: ItemRelink) => void;
  onRemoveChoices: (patch: ItemPatch) => void;
  onCreateField: CreateFieldDoor;
  onInsert: (index: number, item: FormItem) => void;
  onAttach: (itemId: string, index: number, showWhen: FormCondition) => void;
  onSelect: (itemId: string) => void;
  onRule: (itemIds: readonly string[], showWhen: FormCondition) => void;
  onDelete: (itemId: string) => void;
  onRelease: (itemId: string) => void;
  onReplace: (itemId: string, next: FormItem) => void;
  onClose: () => void;
}) {
  const glyph = formItemGlyph(item, customFieldsById);
  const itemField = isFieldItem(item) ? resolveItemField(item, customFieldsById) : null;
  const notice = formItemNotice(item, customFieldsById);
  const question = item.kind === "question" ? item : null;
  const questionType = question?.field.type;
  const isChoice = isChoiceFieldType(questionType);
  const photo = item.kind === "attachment" ? item : null;
  const validated = !isCheckboxItem(item);

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      <Box align="center" direction="row" gap="sm" paddingX="md" paddingY="md">
        <FieldTypeGlyph color={glyph.color} icon={glyph.icon} />
        <Text size="sm" weight="medium">
          {glyph.kindLabel}
        </Text>
        <Box grow />
        {notice ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <Box
                  align="center"
                  aria-label={formItemNoticeSentence(notice)}
                  direction="row"
                  role="img"
                  shrink={false}
                />
              }
            >
              <Icon color="warning" name="alert-triangle" size="sm" />
            </TooltipTrigger>
            <TooltipPopup>{formItemNoticeSentence(notice)}</TooltipPopup>
          </Tooltip>
        ) : null}
        {/* The mark is the panel's, not a ✕: the dialog's own ✕ sits directly above and would read as one control. */}
        <Button aria-label="Close settings" onClick={onClose} size="icon-sm" variant="ghost">
          <Icon name="panel-right-close" size="sm" />
        </Button>
      </Box>
      {isFieldItem(item) ? (
        // The scroll ends on half a pane of spare room so a switch unfolding below it opens in view.
        <PaneScroller>
<Box direction="column">
          <Box direction="column" gap="lg" paddingBottom="lg" paddingTop="xs" paddingX="lg">
            <Field fill>
              <FieldLabel>Display name</FieldLabel>
              <Input
                onChange={(event) => {
                  const label = event.target.value;
                  onChange((current) => ({ ...current, label }));
                }}
                value={item.label}
              />
            </Field>
            <Field fill>
              <FieldLabel>Help text</FieldLabel>
              <Textarea
                onChange={(event) => {
                  const description = event.target.value;
                  onChange((current) => ({ ...current, description }));
                }}
                rows={3}
                value={item.description ?? ""}
              />
            </Field>
            {question && questionType === "date" ? (
              <Field fill>
                <FieldLabel>Format</FieldLabel>
                <OptionSelect
                  aria-label="Format"
                  onValueChange={(value) =>
                    onChange(patchInlineConfig({ includeTime: value === "dateTime" }))
                  }
                  options={DATE_FORMAT_OPTIONS}
                  value={question.field.config?.includeTime === true ? "dateTime" : "date"}
                  width="full"
                />
              </Field>
            ) : null}
            {question && questionType === "number" && !wholeNumbersOnly(question) ? (
              <DecimalsSetting item={question} onChange={onChange} />
            ) : null}
            {question && questionType && TYPED_DEFAULT_TYPES.has(questionType) ? (
              <DefaultValueSetting item={question} onChange={onChange} />
            ) : null}
            <AnswerLinkSettings
              customFieldsById={customFieldsById}
              form={form}
              item={item}
              onRelink={onRelink}
              onCreateField={onCreateField}
            />
            {/* Photos never prefill: a filling's photos are its own. */}
            {(item.kind === "customField" && item.fieldType !== "photos") || item.kind === "property" ? (
              <PrefillSetting item={item} onChange={onChange} />
            ) : null}
          </Box>
          <Separator />
          {question && isChoice ? (
            <>
              <SettingsSection title="Choices">
                <ChoiceSettings item={question} onChange={onChange} onRemoveChoices={onRemoveChoices} />
              </SettingsSection>
              <Separator />
            </>
          ) : (item.kind === "customField" || item.kind === "property") &&
            itemField &&
            isChoiceFieldType(itemField.type) ? (
            <>
              <SettingsSection title="Choices">
                <LinkedChoiceSettings field={itemField} item={item} onChange={onChange} />
              </SettingsSection>
              <Separator />
            </>
          ) : null}
          {validated ? (
            <>
              <SettingsSection title="Validation">
                {isCheckboxItem(item) ? null : (
                  <SwitchRow
                    checked={item.required}
                    label="Required"
                    onCheckedChange={(required) =>
                      onChange((current) =>
                        isFieldItem(current) ? { ...current, required } : current,
                      )
                    }
                  />
                )}
                {question && questionType === "number" ? (
                  <SwitchRow
                    checked={wholeNumbersOnly(question)}
                    label="Whole numbers only"
                    onCheckedChange={(on) =>
                      onChange(
                        patchInlineConfig({ wholeNumbers: on ? true : null } as Partial<
                          NonNullable<CustomFieldDef["config"]>
                        >),
                      )
                    }
                  />
                ) : null}
                {question && (questionType === "number" || questionType === "money") ? (
                  <ConfigRange count={false} item={question} noun="value" onChange={onChange} />
                ) : null}
                {question && (questionType === "text" || questionType === "paragraph") ? (
                  <ConfigRange count item={question} noun="characters" onChange={onChange} />
                ) : null}
                {itemField &&
                isChoiceFieldType(itemField.type) &&
                (item.kind === "customField" ||
                  item.kind === "property" ||
                  item.kind === "question") ? (
                  <RequiredChoicesSetting field={itemField} item={item} onChange={onChange} />
                ) : null}
                {question && questionType === "labels" ? (
                  <ConfigRange
                    ceiling={choiceCount(question.field)}
                    count
                    item={question}
                    maxFloor={question.requiredChoices?.length || undefined}
                    noun="choices"
                    onChange={onChange}
                  />
                ) : null}
                {photo ? <PhotoCountSettings item={photo} onChange={onChange} /> : null}
                {question && (questionType === "date" || questionType === "dateTime") ? (
                  <DateLimitSettings item={question} onChange={onChange} />
                ) : null}
              </SettingsSection>
              <Separator />
            </>
          ) : null}
          <SettingsSection title="Conditions">
            <ConditionNote
              customFieldsById={customFieldsById}
              item={item}
              items={form.items}
              onRelease={onRelease}
              onSelect={onSelect}
            />
            <ConditionalSettings
              customFieldsById={customFieldsById}
              item={item}
              items={form.items}
              onAttach={onAttach}
              onDelete={onDelete}
              onInsert={onInsert}
              onReplace={onReplace}
              onRule={onRule}
            />
          </SettingsSection>
          <PaneScrollGutter />
        </Box>
</PaneScroller>
      ) : (
        <PaneScroller>
<Box direction="column" gap="lg" paddingTop="xs" paddingX="lg">
          <Field fill>
            <FieldLabel>{item.kind === "text" ? "Title" : "Label"}</FieldLabel>
            <Input
              onChange={(event) => {
                const label = event.target.value;
                onChange((current) => ({ ...current, label }));
              }}
              value={item.label}
            />
          </Field>
          {item.kind === "text" ? (
            <Field fill>
              <FieldLabel>Body</FieldLabel>
              <Textarea
                onChange={(event) => {
                  const body = event.target.value;
                  onChange((current) =>
                    current.kind === "text" ? { ...current, body } : current,
                  );
                }}
                rows={5}
                value={item.body}
              />
            </Field>
          ) : (
            <Field fill>
              <FieldLabel>{item.kind === "section" ? "Description" : "Help text"}</FieldLabel>
              <Textarea
                onChange={(event) => {
                  const description = event.target.value;
                  onChange((current) => ({ ...current, description }));
                }}
                rows={3}
                value={item.description ?? ""}
              />
            </Field>
          )}
          {item.kind === "section" ? null : (
            <ConditionNote
              customFieldsById={customFieldsById}
              item={item}
              items={form.items}
              onRelease={onRelease}
              onSelect={onSelect}
            />
          )}
          <PaneScrollGutter />
        </Box>
</PaneScroller>
      )}
    </Box>
  );
}
